import { Panel, RoutedPanelProps } from '@/components/Layout/Panel/Panel';
import { resolveUserName } from '@/features/users/resolveUserName';
import { useUserMap } from '@/features/users/users.hooks';
import { relativeTime } from '@/lib/time';
import { cn } from '@/lib/utils';
import type { Feedback } from '@certhon/models';
import { CheckCircle2, Clock, Megaphone, MessageSquare } from 'lucide-react';
import { useState } from 'react';
import { useMyFeedback } from './feedback.hooks';

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

export const FeedbackPanel = ({
  params: _params,
  path: _path,
  basePath: _basePath,
  ...panelProps
}: RoutedPanelProps) => {
  const { data: feedback, isLoading } = useMyFeedback();

  return (
    <Panel {...panelProps} title="My feedback" icon={Megaphone}>
      {isLoading ? (
        <LoadingState />
      ) : !feedback || feedback.length === 0 ? (
        <EmptyState />
      ) : (
        <FeedbackList items={feedback} />
      )}
    </Panel>
  );
};

// ---------------------------------------------------------------------------
// FeedbackList
// ---------------------------------------------------------------------------

function FeedbackList({ items }: { items: Feedback[] }) {
  const userMap = useUserMap('recnum');

  return (
    <div className="flex flex-col gap-3">
      {items.map((item) => (
        <FeedbackItem
          key={item.id}
          feedback={item}
          handlerName={
            item.handled_user_id
              ? resolveUserName(item.handled_user_id, userMap)
              : null
          }
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FeedbackItem
// ---------------------------------------------------------------------------

const MESSAGE_TRUNCATE_LENGTH = 160;

function FeedbackItem({
  feedback,
  handlerName,
}: {
  feedback: Feedback;
  handlerName: string | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const isLong = feedback.message.length > MESSAGE_TRUNCATE_LENGTH;
  const timeAgo = relativeTime(feedback.timestamp);

  const displayMessage =
    isLong && !expanded
      ? feedback.message.slice(0, MESSAGE_TRUNCATE_LENGTH) + '...'
      : feedback.message;

  return (
    <div
      className={cn(
        'rounded-lg border p-3',
        feedback.handled
          ? 'border-border/50 bg-muted/30'
          : 'border-orange-200 bg-orange-50/50',
      )}
    >
      {/* Header: timestamp + status badge */}
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="text-muted-foreground text-xs">{timeAgo}</span>
        <StatusBadge handled={feedback.handled} handlerName={handlerName} />
      </div>

      {/* Message */}
      <p className="text-foreground/90 text-sm whitespace-pre-wrap">
        {displayMessage}
      </p>
      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="text-primary mt-1 text-xs font-medium hover:underline"
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}

      {/* Footer: template reference */}
      {feedback.proto_list_id && (
        <div className="text-muted-foreground mt-2 flex items-center gap-1 text-xs">
          <MessageSquare className="h-3 w-3" />
          <span>Template question</span>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// StatusBadge
// ---------------------------------------------------------------------------

function StatusBadge({
  handled,
  handlerName,
}: {
  handled: boolean;
  handlerName: string | null;
}) {
  if (handled) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs
          font-medium text-green-700"
      >
        <CheckCircle2 className="h-3 w-3" />
        Handled{handlerName ? ` by ${handlerName}` : ''}
      </span>
    );
  }

  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-xs
        font-medium text-orange-700"
    >
      <Clock className="h-3 w-3" />
      Pending
    </span>
  );
}

// ---------------------------------------------------------------------------
// States
// ---------------------------------------------------------------------------

function LoadingState() {
  return (
    <div className="flex flex-col gap-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="animate-pulse rounded-lg border p-3">
          <div className="mb-2 flex justify-between">
            <div className="bg-muted h-3 w-20 rounded" />
            <div className="bg-muted h-4 w-16 rounded-full" />
          </div>
          <div className="bg-muted h-4 w-3/4 rounded" />
          <div className="bg-muted mt-1 h-4 w-1/2 rounded" />
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-muted-foreground flex flex-col items-center justify-center gap-3 py-12">
      <Megaphone className="h-10 w-10 opacity-30" />
      <p className="text-sm">No feedback submitted yet.</p>
    </div>
  );
}
